import type { Markings } from 'chessboard/plugins/markings/config/types';
import { createTaggedMap } from 'utils/map/tagged-map';
import { getMarkingTags } from 'chessboard/plugins/markings/model/get-marking-tags';
import { intersection } from 'src/utils/set/intersection';
import { markingHasValidSquareOrFromTo } from 'chessboard/plugins/markings/model/marking-has-valid-square-or-from-to';
import { objectsAreShallowEqual } from 'src/utils/object/objects-are-shallow-equal';
import { toArray } from 'utils/array/to-array';
import type JCE from '@chesscom/js-chess-engine';
import { KeyModifiers } from 'chessboard/model/dom/types';
import type { RawAnalysisHighlight } from 'chessboard/plugins/highlights/modules/analysis/types';
import type { RawArrow } from 'chessboard/plugins/arrows/config/types';
import { ArrowType } from 'chessboard/plugins/arrows/config/types';
import { getDefaultOptions } from 'chessboard/model/board/get-default-options';
import type { Board } from 'chessboard/model/board/types';
import { HighlightType } from 'chessboard/plugins/highlights/config/types';

/**
 * Create the markings service. This is framework-agnostic and links to the calling
 * function via callbacks to notify it when markings have been added or removed.
 */
export function createMarkingsService({
  generateKey = generatePrimaryKey,
  context,
  onMarkingAdded,
  onMarkingRemoved,
  onMarkingAddedToAlternateNode,
  onMarkingRemovedFromAlternateNode,
}: Markings.Params = {}): Markings.API {
  const markings = createTaggedMap<Markings.KeyedModel, 'key'>({
    batchCallbacks: true,
    onEntryAdded: onMarkingAdded,
    onEntryRemoved: onMarkingRemoved,
    primaryKey: 'key',
  });

  // all markings get cleared when position changes, so we need to keep separate list for node markings
  const nodeMarkings = createTaggedMap<Markings.KeyedModel, 'id'>({
    batchCallbacks: true,
    primaryKey: 'id',
    onEntryAdded: (entries) => onModifyAlternateNode(entries, onMarkingAddedToAlternateNode),
    onEntryRemoved: (entries) => onModifyAlternateNode(entries, onMarkingRemovedFromAlternateNode),
  });

  /**
   * handle the alternate node callbacks
   */
  function onModifyAlternateNode(
    entries: Markings.KeyedModel | Markings.KeyedModel[],
    callback: undefined | ((entries: Markings.KeyedModel[]) => void),
  ) {
    const arr = toArray(entries);
    const selectedPosition = context?.api.getPosition();
    if (!selectedPosition?.ids) {
      callback?.(arr);
    }

    // we only want these callbacks to fire when they aren't touching the current node,
    // because we want to avoid duplicate callbacks with current node modifications
    const updates = arr.filter((marking) => {
      const nodeIds = getNodeIdsFromMarking(marking);
      return (
        nodeIds?.move !== selectedPosition?.ids.move || nodeIds?.line !== selectedPosition?.ids.line
      );
    });

    if (updates.length) {
      callback?.(updates);
    }
  }

  return {
    addOne,
    addMany,
    getMany: markings.getMany,
    getAll: markings.getAll,
    getAllWhere,
    getOne: markings.getOne,
    removeAll: markings.removeAll,
    removeAllWhere,
    removeOne,
    removeMany,
    toggleMany,
    toggleOne,
    factory: {
      buildStandardAnalysisHighlight,
      buildStandardArrow,
    },
  };

  /**
   * Add many marking.
   */
  function addMany(newMarkings: Markings.Model[]): (string | undefined | null)[] {
    // Calls are batched manually.
    // TODO: Fix batching. Ticket: https://chesscom.atlassian.net/browse/CV-249599
    return addManyWithoutBatching(newMarkings);
  }

  /**
   * Add many markings without batching.
   */
  function addManyWithoutBatching(newMarkings: Markings.Model[]): (string | undefined | null)[] {
    return newMarkings.map(addOne);
  }

  /**
   * Add one marking.
   */
  function addOne(newMarking: Markings.Model): string | undefined | null {
    if (!markingHasValidSquareOrFromTo(newMarking)) {
      return null;
    }

    const tags = getMarkingTags(newMarking);

    const selectedPosition = context?.api.getPosition();

    if (newMarking.node && typeof newMarking.node === 'object') {
      const { move, line } = newMarking.node;
      const mark = withId(withKey(newMarking), { move, line });

      nodeMarkings.addOne(mark, tags);

      const isSame =
        selectedPosition?.ids.move === newMarking.node.move &&
        selectedPosition?.ids.line === newMarking.node.line;
      if (!isSame) {
        return mark.key;
      } else {
        newMarking.node = true;
      }
    }

    const nodeIds = newMarking.node ? selectedPosition?.ids : undefined;
    return markings.addOne(withId(withKey(newMarking), nodeIds), tags);
  }

  /**
   * Remove many markings.
   */
  function removeMany(keysOrMarks: (Markings.KeyedModel | string)[]): boolean {
    return keysOrMarks.every(removeOne);
  }

  /**
   * Remove one marking.
   */
  function removeOne(
    keyOrMark: string | Markings.KeyedModel,
    ids?: Markings.NodeIds | number,
  ): boolean {
    if (typeof keyOrMark !== 'string' && keyOrMark.node && typeof keyOrMark.node === 'object') {
      removeOne(keyOrMark.key, keyOrMark.node);
      return false;
    }

    if (ids && typeof ids === 'object') {
      const { move, line } = ids;
      nodeMarkings.removeOne(`${keyOrMark}-(${move})|(${line})`);
    }

    if (typeof keyOrMark !== 'string') {
      return false;
    }

    return markings.removeOne(keyOrMark);
  }

  /**
   * Get markings with optional filters.
   */
  function getAllWhere(options: Markings.WhereOptions = {}): Markings.KeyedModel[] {
    return <Markings.KeyedModel[]>getKeysWhere(options)
      .map((key) => markings.getMap().get(key))
      .filter(Boolean);
  }

  /**
   * Get markings.
   */
  function getKeysWhere(options: Markings.WhereOptions = {}): string[] {
    const { node, persistent, tags, types } = options;

    let nodeTag: string | undefined;

    if (node !== undefined) {
      nodeTag = node ? 'node' : 'board';
    }

    let persistentTag: string | undefined;

    if (persistent !== undefined) {
      persistentTag = persistent ? 'persistent' : 'temporary';
    }

    const collatedTags = [nodeTag, persistentTag, tags, types].filter((tag) => tag !== undefined);

    if (!collatedTags.length) {
      return Array.from(markings.getMap().keys());
    }

    // Get all the options that have been provided
    const providedOptions = collatedTags
      .map(toArray)
      // Map them to Sets of keys that match ANY of the containing tags
      .map((tagArr) => new Set(markings.getMatchingKeys(<string[]>tagArr)));

    // Get the intersection of all sets
    return Array.from(intersection(...providedOptions));
  }

  /**
   * Remove markings with optional filters.
   */
  function removeAllWhere(options: Markings.WhereOptions = {}): boolean {
    if (options.types?.length === 0) {
      options.types = undefined;
    }

    return markings.removeMany(getKeysWhere(options));
  }

  /**
   * Toggle many markings.
   */
  function toggleMany(
    markingsToToggle: (Markings.Model | Markings.KeyedModel)[],
  ): (ReturnType<Markings.API['removeOne']> | ReturnType<Markings.API['addOne']>)[] {
    return markingsToToggle.map(toggleOne);
  }

  /**
   * Toggle a marking.
   */
  function toggleOne(
    marking: Markings.Model | Markings.KeyedModel,
  ): ReturnType<Markings.API['removeOne']> | ReturnType<Markings.API['addOne']> {
    const keyedMarking = withKey(marking);

    const existingMarking = markings.getOne(keyedMarking.key);

    if (existingMarking && objectsAreShallowEqual(existingMarking.data, keyedMarking.data)) {
      return markings.removeOne(keyedMarking.key);
    } else {
      return addOne(keyedMarking);
    }
  }

  /**
   * Add a key to a marking object.
   */
  function withKey(marking: Markings.Model | Markings.KeyedModel): Markings.KeyedModel {
    return {
      ...marking,
      key: (<Markings.KeyedModel>marking).key || generateKey(marking),
    };
  }

  /**
   * Adds an id to the marking object, with node ids if provided.
   */
  function withId(marking: Markings.KeyedModel, ids?: Markings.NodeIds): Markings.KeyedModel {
    return {
      ...marking,
      id: ids ? `${marking.key}-(${ids.move})|(${ids.line})` : marking.key,
    };
  }

  /**
   * Builds the same analysis highlight payload used when clicking on the board
   */
  function buildStandardAnalysisHighlight(
    square: JCE.SanSquare,
    keyPressed: KeyModifiers = KeyModifiers.None,
  ): RawAnalysisHighlight {
    const options: Board.Options = context?.api.getOptions
      ? context?.api.getOptions()
      : getDefaultOptions();
    return {
      data: {
        keyPressed,
        opacity: options.analysisHighlightOpacity,
        square,
      },
      node: true,
      persistent: false,
      type: HighlightType,
    };
  }

  /**
   * Builds the same arrow payload used when clicking on the board
   */
  function buildStandardArrow(
    from: JCE.SanSquare,
    to: JCE.SanSquare,
    keyPressed: KeyModifiers = KeyModifiers.None,
  ): RawArrow {
    const options: Board.Options = context?.api.getOptions
      ? context?.api.getOptions()
      : getDefaultOptions();
    return {
      data: {
        keyPressed,
        from,
        opacity: options.analysisHighlightOpacity,
        to,
      },
      node: true,
      persistent: false,
      type: ArrowType,
    };
  }
}

/**
 * Generate the primary key for a marking.
 */
export function generatePrimaryKey(marking: Markings.Model) {
  const {
    data: { from, square, to },
    type,
  } = marking;

  if (!square && !(from && to)) {
    throw new Error('Markings that do not have a "square" property must provide a "from" and "to"');
  }

  const key = square || `${from}${to}`;

  return `${type}|${key}`;
}

/**
 * gets the associated node ids from a marking
 */
export function getNodeIdsFromMarking(marking: Markings.KeyedModel): Markings.NodeIds | undefined {
  if (marking.node && typeof marking.node === 'object') {
    return marking.node;
  }

  if (marking.id) {
    // example: "effect|e4-(1)|(5)" -> [1, 5]
    const ids =
      marking.id
        // capture the id numbers from in between the parentheses
        .match(/-\((-?\d+)\)\|\((-?\d+)\)$/)
        // get just the numbers
        ?.slice(1, 3)
        .map(Number) ?? [];

    if (ids.length === 2) {
      return { move: ids[0], line: ids[1] };
    }
  }

  return undefined;
}
